/**
 * @fileoverview Product Analytics - Heatmaps & Click Tracking
 * @module features/analytics/heatmaps
 * 
 * @description
 * Track user interactions for heatmap visualization including
 * clicks, mouse movements, and scroll behavior.
 * 
 * @useCases
 * - Visualize click patterns on pages
 * - Identify dead zones and ignored elements
 * - Analyze scroll depth and attention
 * - Optimize CTA placement
 * - Improve page layout based on behavior
 * 
 * @metrics
 * - Click coordinates and density
 * - Scroll depth distribution
 * - Attention time by viewport
 * - Element interaction rates
 * - Rage clicks detection
 * 
 * @chartData
 * - Heatmap: Click density overlay
 * - Scroll map: Attention by page section
 * - Element report: Interaction by element
 * - Rage click detection
 * 
 * @example
 * ```typescript
 * const heatmaps = new Heatmaps(webId, endpoint, apiKey);
 * 
 * // Track click
 * await heatmaps.trackClick({
 *   pageUrl: '/pricing',
 *   x: 450,
 *   y: 320,
 *   viewportWidth: 1920,
 *   viewportHeight: 1080
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { DateRangeFilter } from "../../types";
import { getWindow, getDocument, isBrowser, combineTeardowns, Teardown } from "../../utils/browser";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Click interaction data.
 */
export interface ClickData {
  /** Page URL */
  pageUrl: string;
  /** X coordinate (from left) */
  x: number;
  /** Y coordinate (from top) */
  y: number;
  /** Viewport width */
  viewportWidth: number;
  /** Viewport height */
  viewportHeight: number;
  /** Element selector clicked */
  elementSelector?: string;
  /** Element text (truncated) */
  elementText?: string;
  /** User ID */
  userId?: string;
  /** Session ID */
  sessionId?: string;
}

/**
 * Movement/hover data.
 */
export interface MovementData {
  /** Page URL */
  pageUrl: string;
  /** Movement path (array of x,y coordinates) */
  path: Array<{ x: number; y: number; t: number }>;
  /** Viewport dimensions */
  viewport: { width: number; height: number };
  /** Session ID */
  sessionId?: string;
}

/**
 * Scroll data.
 */
export interface ScrollData {
  /** Page URL */
  pageUrl: string;
  /** Maximum scroll depth reached (percentage) */
  maxScrollDepth: number;
  /** Page height */
  pageHeight: number;
  /** Viewport height */
  viewportHeight: number;
  /** Time at various scroll depths */
  timeAtDepths?: Record<number, number>;
  /** Session ID */
  sessionId?: string;
}

/**
 * Heatmap response from API.
 */
export interface HeatmapResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    recorded: boolean;
    interactionId: string;
  };
  timestamp: string;
}

/**
 * Heatmap data for visualization.
 */
export interface HeatmapData {
  /** Page URL */
  pageUrl: string;
  /** Click points for heatmap */
  clicks: Array<{
    x: number;
    y: number;
    count: number;
    normalizedX: number;
    normalizedY: number;
  }>;
  /** Scroll depth data */
  scrollDepth: Array<{
    depth: number;
    usersReached: number;
    percentageReached: number;
    avgTimeAtDepth: number;
  }>;
  /** Element interaction data */
  elements: Array<{
    selector: string;
    clicks: number;
    hovers: number;
    avgTimeHovered: number;
  }>;
  /** Rage click zones */
  rageClicks: Array<{
    x: number;
    y: number;
    count: number;
    element: string;
  }>;
  /** Summary */
  summary: {
    totalClicks: number;
    uniqueSessions: number;
    avgScrollDepth: number;
    rageClickRate: number;
  };
}

// ============================================================================
// HEATMAPS CLASS
// ============================================================================

/**
 * Heatmaps - Track user interactions for heatmap visualization.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides heatmap data collection including:
 * - Click tracking with coordinates
 * - Mouse movement tracking
 * - Scroll depth tracking
 * - Element interaction tracking
 * - Rage click detection
 * 
 * @example
 * ```typescript
 * const heatmaps = new Heatmaps(webId, endpoint, apiKey);
 * 
 * // Track clicks
 * document.addEventListener('click', (e) => {
 *   heatmaps.trackClick({
 *     pageUrl: window.location.pathname,
 *     x: e.clientX,
 *     y: e.clientY + window.scrollY,
 *     viewportWidth: window.innerWidth,
 *     viewportHeight: window.innerHeight,
 *     elementSelector: getSelector(e.target)
 *   });
 * });
 * 
 * // Get heatmap data for visualization
 * const data = await heatmaps.getData('/pricing', { range: 'last7days' });
 * ```
 */
export class Heatmaps extends FlowGridTransport {

  /** Throttle timestamps */
  private lastMovementTime = 0;
  private lastScrollTime = 0;

  /** Throttle intervals (ms) */
  private static readonly MOVEMENT_THROTTLE_MS = 100;
  private static readonly SCROLL_THROTTLE_MS = 500;

  /**
   * Tracks a click interaction.
   * 
   * @param data - Click data
   * @returns Promise resolving to heatmap response
   */
  async trackClick(data: ClickData): Promise<HeatmapResponse> {
    return await this.init("heatmap_click", {
      type: "heatmap_click",
      pageUrl: data.pageUrl,
      x: data.x,
      y: data.y,
      normalizedX: data.x / data.viewportWidth,
      normalizedY: data.y / data.viewportHeight,
      viewportWidth: data.viewportWidth,
      viewportHeight: data.viewportHeight,
      elementSelector: data.elementSelector,
      elementText: data.elementText?.substring(0, 50),
      userId: data.userId,
      sessionId: data.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks mouse movement path.
   * 
   * @param data - Movement data
   * @returns Promise resolving to heatmap response
   */
  async trackMovement(data: MovementData): Promise<HeatmapResponse> {
    const now = Date.now();
    if (now - this.lastMovementTime < Heatmaps.MOVEMENT_THROTTLE_MS) {
      return { eventName: 'heatmap_movement', properties: {}, response: { recorded: false, interactionId: '' }, timestamp: new Date().toISOString() };
    }
    this.lastMovementTime = now;

    return await this.init("heatmap_movement", {
      type: "heatmap_movement",
      pageUrl: data.pageUrl,
      path: data.path,
      viewport: data.viewport,
      sessionId: data.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks scroll behavior.
   * 
   * @param data - Scroll data
   * @returns Promise resolving to heatmap response
   */
  async trackScroll(data: ScrollData): Promise<HeatmapResponse> {
    const now = Date.now();
    if (now - this.lastScrollTime < Heatmaps.SCROLL_THROTTLE_MS) {
      return { eventName: 'heatmap_scroll', properties: {}, response: { recorded: false, interactionId: '' }, timestamp: new Date().toISOString() };
    }
    this.lastScrollTime = now;

    return await this.init("heatmap_scroll", {
      type: "heatmap_scroll",
      pageUrl: data.pageUrl,
      maxScrollDepth: data.maxScrollDepth,
      pageHeight: data.pageHeight,
      viewportHeight: data.viewportHeight,
      timeAtDepths: data.timeAtDepths,
      sessionId: data.sessionId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks rage clicks (repeated clicks in frustration).
   * 
   * @param pageUrl - Page URL
   * @param x - X coordinate
   * @param y - Y coordinate
   * @param clickCount - Number of rapid clicks
   * @returns Promise resolving to heatmap response
   */
  async trackRageClick(
    pageUrl: string,
    x: number,
    y: number,
    clickCount: number
  ): Promise<HeatmapResponse> {
    return await this.init("heatmap_rage_click", {
      type: "heatmap_rage_click",
      pageUrl,
      x,
      y,
      clickCount,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Auto-collect heatmap interactions: clicks (with element selector + text)
   * and scroll depth on the current page. Mouse-move tracking is opt-in
   * because the volume can be very high.
   *
   * @param options.movement Subscribe to throttled mousemove sampling.
   * @param options.userId   Attached to every event.
   * @returns Teardown function detaching all listeners.
   */
  autoTrack(options: { movement?: boolean; userId?: string; sessionId?: string } = {}): Teardown {
    if (!isBrowser()) return () => { /* no-op on the server */ };
    const win = getWindow()!;
    const doc = getDocument()!;

    const selectorOf = (el: Element | null): string => {
      if (!el) return "";
      if (el.id) return `#${el.id}`;
      const tag = el.tagName.toLowerCase();
      const cls = (el.className && typeof el.className === "string")
        ? "." + el.className.trim().split(/\s+/).slice(0, 2).join(".")
        : "";
      return `${tag}${cls}`;
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      void this.trackClick({
        pageUrl: win.location.pathname,
        x: e.clientX,
        y: e.clientY + win.scrollY,
        viewportWidth: win.innerWidth,
        viewportHeight: win.innerHeight,
        elementSelector: selectorOf(target),
        elementText: (target?.textContent || "").trim().slice(0, 50),
        userId: options.userId,
        sessionId: options.sessionId,
      });
    };
    doc.addEventListener("click", onClick, { capture: true, passive: true });

    let maxDepth = 0;
    let scrollFlushScheduled = false;
    const flushScroll = () => {
      scrollFlushScheduled = false;
      const docEl = doc.documentElement;
      void this.trackScroll({
        pageUrl: win.location.pathname,
        maxScrollDepth: maxDepth,
        pageHeight: docEl.scrollHeight,
        viewportHeight: win.innerHeight,
        sessionId: options.sessionId,
      });
    };
    const onScroll = () => {
      const docEl = doc.documentElement;
      const top = win.scrollY || docEl.scrollTop;
      const height = Math.max(1, docEl.scrollHeight - win.innerHeight);
      const depth = Math.min(100, Math.round((top / height) * 100));
      if (depth > maxDepth) maxDepth = depth;
      if (!scrollFlushScheduled) {
        scrollFlushScheduled = true;
        win.addEventListener("pagehide", flushScroll, { once: true });
      }
    };
    win.addEventListener("scroll", onScroll, { passive: true });

    let moveTeardown: (() => void) | undefined;
    if (options.movement) {
      let buffer: Array<{ x: number; y: number; t: number }> = [];
      const start = Date.now();
      const onMove = (e: MouseEvent) => {
        buffer.push({ x: e.clientX, y: e.clientY, t: Date.now() - start });
        if (buffer.length >= 50) {
          const path = buffer; buffer = [];
          void this.trackMovement({
            pageUrl: win.location.pathname,
            path,
            viewport: { width: win.innerWidth, height: win.innerHeight },
            sessionId: options.sessionId,
          });
        }
      };
      doc.addEventListener("mousemove", onMove, { passive: true });
      moveTeardown = () => doc.removeEventListener("mousemove", onMove);
    }

    return combineTeardowns(
      () => doc.removeEventListener("click", onClick, { capture: true } as EventListenerOptions),
      () => win.removeEventListener("scroll", onScroll),
      () => win.removeEventListener("pagehide", flushScroll),
      moveTeardown,
    );
  }
}

export default Heatmaps;
